import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthResponse, UserSummary } from '../types';

type AuthStatus = 'idle' | 'loading' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: UserSummary | null;
  accessToken: string | null;
  refreshToken: string | null;
  error: string | null;
}

const canUseStorage =
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readStorageValue = (key: string): string | null => {
  if (!canUseStorage) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`读取本地存储字段 ${key} 失败`, error);
    return null;
  }
};

const writeStorageValue = (key: string, value: string) => {
  if (!canUseStorage) {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`写入本地存储字段 ${key} 失败`, error);
  }
};

const removeStorageValue = (key: string) => {
  if (!canUseStorage) {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.warn(`移除本地存储字段 ${key} 失败`, error);
  }
};

const readStoredUser = (): UserSummary | null => {
  const raw = readStorageValue('auth_user');
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as UserSummary;
  } catch (error) {
    console.warn('解析存储的用户信息失败，将清理该字段', error);
    removeStorageValue('auth_user');
    return null;
  }
};

const initialAccessToken = readStorageValue('access_token');
const initialRefreshToken = readStorageValue('refresh_token');
const initialUser = readStoredUser();

const initialState: AuthState = {
  status: initialAccessToken ? 'authenticated' : 'idle',
  user: initialUser,
  accessToken: initialAccessToken,
  refreshToken: initialRefreshToken,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    startAuth(state) {
      state.status = 'loading';
      state.error = null;
    },
    authSuccess(state, action: PayloadAction<AuthResponse>) {
      const payload = action.payload;
      state.status = 'authenticated';
      state.error = null;
      state.user = payload.user;
      state.accessToken = payload.access_token;
      state.refreshToken = payload.refresh_token;

      if (payload.access_token) {
        writeStorageValue('access_token', payload.access_token);
      }
      if (payload.refresh_token) {
        writeStorageValue('refresh_token', payload.refresh_token);
      }
      writeStorageValue('auth_user', JSON.stringify(payload.user));
    },
    authFailure(state, action: PayloadAction<string>) {
      state.status = state.accessToken ? 'authenticated' : 'idle';
      state.error = action.payload;
    },
    clearAuthError(state) {
      state.error = null;
    },
    hydrateFromStorage(state) {
      const storedAccess = readStorageValue('access_token');
      const storedRefresh = readStorageValue('refresh_token');
      const storedUser = readStoredUser();

      state.accessToken = storedAccess;
      state.refreshToken = storedRefresh;
      state.user = storedUser;
      state.status = storedAccess ? 'authenticated' : 'idle';
      state.error = null;
    },
    logout(state) {
      state.status = 'idle';
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.error = null;
      removeStorageValue('access_token');
      removeStorageValue('refresh_token');
      removeStorageValue('auth_user');
    },
  },
});

export const {
  startAuth,
  authSuccess,
  authFailure,
  clearAuthError,
  hydrateFromStorage,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
